"use client";

import io from 'socket.io-client';

// Define the events that match the backend implementation
interface ServerToClientEvents {
  'message:new': (data: any) => void;
  'message:updated': (data: any) => void;
  'message:deleted': (data: any) => void;
  'conversation:updated': (data: any) => void;
  'user:online': (data: { userId: string }) => void;
  'user:offline': (data: { userId: string }) => void;
  'webrtc:user-joined': (data: { callerId: string; isVideo: boolean }) => void;
  'webrtc:user-left': (data: { callerId: string }) => void;
  'webrtc:offer': (data: { offer: RTCSessionDescriptionInit; callerId: string; roomId: string; isVideo: boolean }) => void;
  'webrtc:answer': (data: { answer: RTCSessionDescriptionInit; callerId: string; roomId: string }) => void;
  'webrtc:ice-candidate': (data: { candidate: RTCIceCandidateInit; callerId: string; roomId: string }) => void;
  'webrtc:call-ended': (data: { callerId: string; roomId: string }) => void;
  'notification:new': (data: any) => void;
  'post:new': (data: any) => void;
  'post:updated': (data: any) => void;
  'post:deleted': (data: any) => void;
}

interface ClientToServerEvents {
  'message:send': (data: any) => void;
  'message:typing': (data: any) => void;
  'message:read': (data: any) => void;
  'webrtc:join-room': (data: { roomId: string; isVideo: boolean }) => void;
  'webrtc:leave-room': (data: { roomId: string }) => void;
  'webrtc:offer': (data: { offer: RTCSessionDescriptionInit; recipientId: string; callerId: string; roomId: string; isVideo: boolean }) => void;
  'webrtc:answer': (data: { answer: RTCSessionDescriptionInit; recipientId: string; callerId: string; roomId: string }) => void;
  'webrtc:ice-candidate': (data: { candidate: RTCIceCandidateInit; recipientId: string; callerId: string; roomId: string }) => void;
  'webrtc:call-ended': (data: { recipientId: string; roomId: string }) => void;
}

// Type for the socket instance
type SocketType = ReturnType<typeof io>;

// Create a singleton socket instance
let socket: SocketType | null = null;

/**
 * Initialize the socket connection to endureapi
 */
export const initSocket = (): SocketType | null => {
  if (typeof window === "undefined") {
    console.log('[Socket] Not initializing in server environment');
    return null;
  }

  if (!socket) {
    const apiUrl = process?.env?.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
    
    console.log('[Socket] Initializing connection to:', apiUrl);
    
    // Get JWT token for authentication
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    socket = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        token: token
      },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    // Connection event handlers
    socket.on('connect', () => {
      console.log('[Socket] Connected to endureapi:', socket?.id);
    });

    socket.on('disconnect', (reason: string) => {
      console.log('[Socket] Disconnected from endureapi:', reason);
    });

    socket.on('connect_error', (error: Error) => {
      console.error('[Socket] Connection error:', error);
    });

    socket.on('reconnect', (attemptNumber: number) => {
      console.log('[Socket] Reconnected to endureapi after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_error', (error: Error) => {
      console.error('[Socket] Reconnection error:', error);
    });

    socket.on('reconnect_failed', () => {
      console.error('[Socket] Failed to reconnect to endureapi');
    });
  }

  return socket;
};

/**
 * Get the current socket instance
 */
export const getSocket = (): SocketType | null => {
  if (typeof window === "undefined") {
    return null;
  }
  
  return socket;
};

/**
 * Check if the socket is connected
 */
export const isSocketConnected = (): boolean => {
  return socket?.connected || false;
};

/**
 * Disconnect the socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    console.log('[Socket] Disconnecting from endureapi');
    socket.disconnect();
    socket = null;
  }
};

/**
 * Emit an event to the socket server
 */
export const emitSocketEvent = <T extends keyof ClientToServerEvents>(
  event: T, 
  data: Parameters<ClientToServerEvents[T]>[0]
): boolean => {
  if (!socket || !socket.connected) {
    console.warn('[Socket] No connected socket instance available');
    return false;
  }
  
  console.log('[Socket] Emitting event:', event, data);
  socket.emit(event, data);
  return true;
};

/**
 * Add a listener for a socket event
 */
export const addSocketListener = <T extends keyof ServerToClientEvents>(
  event: T,
  listener: ServerToClientEvents[T]
): boolean => {
  if (!socket) {
    console.warn('[Socket] No socket instance available');
    return false;
  }
  
  console.log('[Socket] Adding listener for event:', event);
  socket.on(event, listener);
  return true;
};

/**
 * Remove a listener for a socket event
 */
export const removeSocketListener = <T extends keyof ServerToClientEvents>(
  event: T,
  listener: ServerToClientEvents[T]
): boolean => {
  if (!socket) {
    return false;
  }
  
  console.log('[Socket] Removing listener for event:', event);
  socket.off(event, listener);
  return true;
};

/**
 * Update socket authentication with JWT token
 */
export const updateSocketAuth = (token: string): void => {
  if (socket) {
    console.log('[Socket] Updating auth with new token');
    // Disconnect and reconnect with new auth
    socket.disconnect();
    socket = null;
    // Store token and reinitialize
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
    initSocket();
  }
};

// Export the socket instance as default
export default socket;
